import React, { useEffect, useState } from 'react';

const MACRO_SENSITIVITY_LABELS = {
  'loanGrowth.gdp': 'GDP to loan growth',
  'depositGrowth.runoff': 'Runoff to deposit growth',
  'depositCost.runoff': 'Runoff to deposit cost',
  'expense.expenseInflation': 'Expense inflation to NIE',
  'provision.creditLoss': 'Credit loss to provision'
};

/** Presentation-only levers view for the regional forecasting workspace. */
const RegionalLeversStage = ({ vm }) => {
  const {
    Download,
    ForecastOp03Suite,
    Gauge,
    MACRO_SENSITIVITY_COEFFICIENTS = [],
    NumericInput,
    RefreshCw,
    SlidersHorizontal,
    SourceBadge,
    activeExpenseLeverCount,
    activeProfile,
    activeShockCount,
    activeWorkspaceStage,
    adjustedMonthlySeries,
    baselineMonthlySeries,
    canEditSubmission,
    expenseInputs,
    feeDrivers,
    formatLeverValue,
    formatPercent,
    isAggregate,
    latestActualMonth,
    macroCase,
    quarterlyGrowth,
    remainderYear,
    resetExpenseInputs,
    resetMacroLevers,
    selectedMarket,
    setYearLeverAll,
    shocks,
    submission,
    submissionId,
    updateExpenseInput,
    updateFeeDriver,
    updateLeverAttentionCount,
    updateQuarterlyGrowth,
    updateShock,
    updateYearLever,
    yearAdjustments
  } = vm;
  const [creditStressDraft, setCreditStressDraft] = useState(shocks.creditStressBps);
  const [creditStressDragging, setCreditStressDragging] = useState(false);

  useEffect(() => {
    if (!creditStressDragging) setCreditStressDraft(shocks.creditStressBps);
  }, [creditStressDragging, shocks.creditStressBps]);

  const commitCreditStress = () => {
    setCreditStressDragging(false);
    const nextValue = Number(creditStressDraft);
    if (Number.isFinite(nextValue) && nextValue !== Number(shocks.creditStressBps)) {
      updateShock('creditStressBps', nextValue);
    }
  };

  const commitCreditStressOnKeyUp = (event) => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Enter', ' '].includes(event.key)) {
      commitCreditStress();
    }
  };

  return (
    <>
{activeWorkspaceStage === 'levers' && <section className="forecast-panel forecast-existing-adjustments workspace-stage-panel" id="regional-stage-levers" tabIndex="-1">
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_03 · Existing baseline adjustments</span>
              <h2>Review exceptions, then set business and expense levers</h2>
            </div>
            <SlidersHorizontal size={18} />
          </summary>

          <div className="forecast-accordion-body">

        <ForecastOp03Suite
          planningEntity={isAggregate ? null : submissionId ? submission?.planning_entity || null : selectedMarket.planningEntity}
          cycleCode={submission?.cycle_code || 'FY2027_BUDGET'}
          division={selectedMarket.region}
          market={selectedMarket.market}
          author={activeProfile.name}
          baselineMonthly={baselineMonthlySeries}
          adjustedMonthly={adjustedMonthlySeries}
          yearAdjustments={yearAdjustments}
          onLeverChange={updateYearLever}
          onLeverSetAll={setYearLeverAll}
          quarterlyGrowth={quarterlyGrowth}
          onQuarterlyGrowthChange={updateQuarterlyGrowth}
          remainderYear={remainderYear}
          latestActualMonth={latestActualMonth}
          quarterlyDetailEnabled={!isAggregate}
          macroCase={macroCase}
          feeDrivers={feeDrivers}
          onDriverChange={updateFeeDriver}
          onAttentionCount={updateLeverAttentionCount}
          disabled={Boolean(submissionId) && !canEditSubmission}
        />

        <div className="forecast-grid forecast-grid-3 forecast-grid-align-start">
          <div className="forecast-panel-lite">
            <div className="forecast-panel-head">
              <div>
                <span className="forecast-section-code">OP_03B · Financial levers</span>
                <h3>Credit, liquidity, pricing, and appetite</h3>
              </div>
              <Gauge size={18} />
            </div>

            <div className="forecast-shock-grid forecast-lever-grid">
              <div className="forecast-lever-card-toolbar forecast-lever-card-toolbar-wide">
                <SourceBadge kind={activeShockCount > 0 ? 'macro' : 'central'}>
                  {activeShockCount} active shock levers
                </SourceBadge>
                <button type="button" className="forecast-secondary-action" onClick={resetMacroLevers}>
                  <RefreshCw size={14} />
                  Reset
                </button>
              </div>
              <div className="forecast-central-macro">
                <p className="forecast-submit-note" style={{ marginTop: 0 }}>
                  Dictated by the central macro scenario (read-only here — switch shock scenario tabs to change):
                </p>
                <div className="forecast-submit-chips">
                  <span className="forecast-health-chip neutral">
                    Parallel rate shock: {shocks.rateShockBps > 0 ? '+' : ''}{shocks.rateShockBps} bps · CENTRAL
                  </span>
                  <span className="forecast-health-chip neutral">
                    GLP / GDP shock: {shocks.gdpShock > 0 ? '+' : ''}{Number(shocks.gdpShock).toFixed(1)} pts · CENTRAL
                  </span>
                  <span className="forecast-health-chip neutral">
                    Deposit runoff stress: {formatPercent(shocks.liquidityRunoffPct)} · CENTRAL
                  </span>
                  <span className="forecast-health-chip neutral">
                    Deposit beta / pricing lag: {shocks.depositBetaShockPct > 0 ? '+' : ''}{shocks.depositBetaShockPct} pts · CENTRAL
                  </span>
                  <span className="forecast-health-chip neutral">
                    Loan growth appetite cap: {formatPercent(shocks.loanGrowthLimiterPct)} · monitored in guardrails
                  </span>
                </div>
                <details className="forecast-submit-note" open style={{ marginTop: 10 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Read-only macro sensitivity legend</summary>
                  <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                    {MACRO_SENSITIVITY_COEFFICIENTS.map((item) => (
                      <div
                        key={item.key}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          alignItems: 'baseline'
                        }}
                      >
                        <strong>{MACRO_SENSITIVITY_LABELS[item.key] || item.key}</strong>
                        <span className="forecast-health-chip neutral">{item.coefficient}</span>
                        <small>{item.description} <span className="forecast-mono">{item.formulaKey}</span></small>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
              <label>
                Credit stress / NCO uplift
                <input
                  type="range"
                  min="0"
                  max="120"
                  step="5"
                  value={creditStressDraft}
                  onChange={(event) => setCreditStressDraft(event.target.value)}
                  onMouseDown={() => setCreditStressDragging(true)}
                  onMouseUp={commitCreditStress}
                  onTouchStart={() => setCreditStressDragging(true)}
                  onTouchEnd={commitCreditStress}
                  onKeyUp={commitCreditStressOnKeyUp}
                  onBlur={commitCreditStress}
                />
                <strong>+{creditStressDraft} bps NCO</strong>
              </label>
            </div>
          </div>

          <div className="forecast-panel-lite">
            <div className="forecast-panel-head">
              <div>
                <span className="forecast-section-code">OP_03C · Controllable spend</span>
                <h3>Market-controlled expense levers</h3>
              </div>
              <Download size={18} />
            </div>
            <div className="forecast-expense-list">
              <label className={shocks.expenseShockPct !== 0 ? 'forecast-lever-active' : ''}>
                <span>
                  <strong>Key vendor inflation</strong>
                  <small>Contract repricing pressure applied to the operating expense base (%)</small>
                </span>
                <NumericInput
                  className=""
                  min={0}
                  max={6}
                  step={0.1}
                  value={shocks.expenseShockPct}
                  onCommit={(value) => updateShock('expenseShockPct', value)}
                />
              </label>
              <div className="forecast-lever-card-toolbar">
                <SourceBadge kind={activeExpenseLeverCount > 0 ? 'market' : 'central'}>
                  {activeExpenseLeverCount} active spend levers
                </SourceBadge>
                <button type="button" className="forecast-secondary-action" onClick={resetExpenseInputs}>
                  <RefreshCw size={14} />
                  Reset
                </button>
              </div>
              {Object.entries(expenseInputs).map(([leverId, lever]) => (
                <label className={lever.value !== lever.centralBudget ? 'forecast-lever-active' : ''} key={leverId}>
                  <span>
                    <strong>{lever.label}</strong>
                    <small>
                      GL {lever.gl} / base {formatLeverValue(lever, lever.centralBudget)} / limit {formatLeverValue(lever, lever.centralLimit)}
                    </small>
                  </span>
                  <NumericInput
                    className=""
                    min={0}
                    step={leverId === 'legalRecoupment' ? 1 : 0.1}
                    value={lever.value}
                    onCommit={(value) => updateExpenseInput(leverId, value)}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

          </div>
        </details>
      </section>}
    </>
  );
};

export default RegionalLeversStage;
